<template>
    <Page v-if="stek" class="stek-settings-page">
        <template #title>
            <div class="title-section">
                <i class="pi pi-cog settings-icon"></i>
                <h1 class="page-title">Stek Settings</h1>
            </div>
        </template>

        <!-- General Section -->
        <Section class="general-section">
            <template #title>General</template>

            <div class="info-field">
                <label class="info-label">Organization ID</label>
                <div class="info-value-row">
                    <span class="info-value">{{
                        organization?.identity ?? "—"
                    }}</span>
                    <Button
                        class="copy-btn"
                        text
                        :label="orgIdCopied ? 'Copied!' : 'Copy'"
                        :disabled="!organization?.identity"
                        @click="copyOrgId"
                        aria-label="Copy Organization ID to clipboard"
                    >
                        <template #icon>
                            <span class="copy-icon" aria-hidden="true">
                                <i
                                    class="pi pi-copy copy-icon-glyph"
                                    :class="{ 'is-active': !orgIdCopied }"
                                ></i>
                                <i
                                    class="pi pi-check copy-icon-glyph copy-icon-check"
                                    :class="{ 'is-active': orgIdCopied }"
                                ></i>
                            </span>
                        </template>
                    </Button>
                </div>
            </div>
        </Section>

        <!-- Enterprise Features Section -->
        <Section class="enterprise-section">
            <template #title>Enterprise Features</template>

            <div class="enterprise-notice">
                <i class="pi pi-info-circle"></i>
                <p>
                    These features require an enterprise subscription.
                    <a
                        href="https://stekz.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Contact us for the possibilities.
                    </a>
                </p>
            </div>

            <div class="features-grid">
                <!-- Live Mode Feature -->
                <Card class="feature-card">
                    <template #title>
                        <div class="feature-header">
                            <i class="pi pi-chart-line feature-icon"></i>
                            <span>Live Mode</span>
                        </div>
                    </template>
                    <template #content>
                        <p class="feature-description">
                            Monitor your running processes in real-time. View
                            metrics, track process instances, and analyze
                            performance data.
                        </p>
                        <div class="feature-toggle">
                            <div class="toggle-wrapper">
                                <ToggleSwitch
                                    v-model="liveModeEnabled"
                                    inputId="live-mode-toggle"
                                    :disabled="!isEnterprise || saving"
                                    @change="saveLiveModeSettings"
                                />
                                <label for="live-mode-toggle">
                                    {{
                                        liveModeEnabled ? "Enabled" : "Disabled"
                                    }}
                                </label>
                            </div>
                        </div>
                    </template>
                </Card>

                <!-- Compliance Tracking Feature -->
                <Card
                    class="feature-card"
                    :class="{ 'muted-feature': !liveModeEnabled }"
                >
                    <template #title>
                        <div class="feature-header">
                            <i class="pi pi-shield feature-icon"></i>
                            <span>Compliance Tracking</span>
                        </div>
                    </template>
                    <template #content>
                        <p class="feature-description">
                            Track Operating Effectiveness with audit evidence.
                            Attach proof of execution to process events for
                            regulatory compliance.
                        </p>

                        <div
                            v-if="!liveModeEnabled"
                            class="prerequisite-message"
                        >
                            <i class="pi pi-exclamation-triangle"></i>
                            <span>Requires Live Mode to be enabled</span>
                        </div>

                        <div class="feature-toggle">
                            <div class="toggle-wrapper">
                                <ToggleSwitch
                                    v-model="complianceEnabled"
                                    inputId="compliance-toggle"
                                    :disabled="
                                        !isEnterprise ||
                                        saving ||
                                        !liveModeEnabled
                                    "
                                    @change="saveComplianceSettings"
                                />
                                <label for="compliance-toggle">
                                    {{
                                        complianceEnabled
                                            ? "Enabled"
                                            : "Disabled"
                                    }}
                                </label>
                            </div>
                        </div>
                    </template>
                </Card>
            </div>

            <Message
                v-if="!isEnterprise"
                severity="info"
                :closable="false"
                class="upgrade-message"
            >
                <template #icon>
                    <i class="pi pi-lock"></i>
                </template>
                <div class="upgrade-content">
                    <strong>Upgrade to Enterprise</strong>
                    <p>
                        Enable these features by upgrading to an Enterprise
                        subscription.
                    </p>
                    <Button
                        label="Contact Sales"
                        icon="pi pi-envelope"
                        size="small"
                        @click="contactSales"
                    />
                </div>
            </Message>
        </Section>

        <!-- Releases Section -->
        <Section class="releases-section">
            <template #title>Releases</template>
            <ReleasesList />
        </Section>

        <!-- Integrations Section -->
        <Section class="integrations-section">
            <template #title>Integrations</template>

            <div class="integrations-content">
                <p class="section-description">
                    Connect external tools and services to your Stek. Configure
                    integrations for version control, project management, and
                    more.
                </p>

                <Button
                    label="Manage Integrations"
                    icon="pi pi-link"
                    @click="navigateToIntegrations"
                />
            </div>
        </Section>
    </Page>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Card from "primevue/card";
import Message from "primevue/message";
import ToggleSwitch from "primevue/toggleswitch";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";
import { useRouter } from "vue-router";

import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import ReleasesList from "@/components/releases/ReleasesList.vue";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useServices } from "@/lib/openapi";
import { BodyStartStekStekMgtManageStekStartPost } from "@/lib/openapi/gen";

const router = useRouter();
const toast = useToast();
const { stek } = useStekManager();
const { organization } = useOrganizationManager();

// Copy Organization ID to clipboard
const orgIdCopied = ref(false);
const copyOrgId = () => {
    if (!organization.value?.identity) return;
    navigator.clipboard.writeText(organization.value.identity);
    orgIdCopied.value = true;
    window.setTimeout(() => {
        orgIdCopied.value = false;
    }, 2000);
};

const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

// Enterprise status (hardcoded for now - would come from subscription service)
const isEnterprise = ref(true); // Set to false to test locked state

// Feature toggles
const liveModeEnabled = ref(true); // Live mode is always available for now
const complianceEnabled = ref(stek.value?.compliance_enabled ?? false);

const saving = ref(false);

// Watch for stek changes
watch(
    () => stek.value,
    (newStek) => {
        if (newStek) {
            complianceEnabled.value = newStek.compliance_enabled ?? false;
            liveModeEnabled.value = newStek.live_mode_enabled ?? true;
        }
    },
    { immediate: true }
);

const saveLiveModeSettings = async () => {
    if (!stek.value) return;

    saving.value = true;
    try {
        // If live mode is being disabled, also disable compliance tracking
        const updates: BodyStartStekStekMgtManageStekStartPost = {
            stek_id: stek.value.identity,
            live_mode_enabled: liveModeEnabled.value,
        };

        if (!liveModeEnabled.value && complianceEnabled.value) {
            updates.compliance_enabled = false;
        }

        const { response } = await api.startStekStekMgtManageStekStartPost({
            body: updates,
        });

        if (response.ok) {
            // Update the stek store with the new value
            if (stek.value) {
                stek.value.live_mode_enabled = liveModeEnabled.value;
                if (!liveModeEnabled.value && complianceEnabled.value) {
                    stek.value.compliance_enabled = false;
                    complianceEnabled.value = false;
                }
            }

            toast.add({
                severity: "success",
                summary: "Settings Saved",
                detail: `Live mode ${liveModeEnabled.value ? "enabled" : "disabled"}`,
                life: 3000,
            });
        } else {
            throw new Error("Failed to update settings");
        }
    } catch (error) {
        console.error("Error saving settings:", error);
        // Revert the toggle on error
        liveModeEnabled.value = !liveModeEnabled.value;
        toast.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to save settings. Please try again.",
            life: 5000,
        });
    } finally {
        saving.value = false;
    }
};

const saveComplianceSettings = async () => {
    if (!stek.value) return;

    saving.value = true;
    try {
        const { response } = await api.startStekStekMgtManageStekStartPost({
            body: {
                stek_id: stek.value.identity,
                compliance_enabled: complianceEnabled.value,
            },
        });

        if (response.ok) {
            // Update the stek store with the new value
            if (stek.value) {
                stek.value.compliance_enabled = complianceEnabled.value;
            }

            toast.add({
                severity: "success",
                summary: "Settings Saved",
                detail: `Compliance tracking ${complianceEnabled.value ? "enabled" : "disabled"}`,
                life: 3000,
            });
        } else {
            throw new Error("Failed to update settings");
        }
    } catch (error) {
        console.error("Error saving settings:", error);
        // Revert the toggle on error
        complianceEnabled.value = !complianceEnabled.value;
        toast.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to save settings. Please try again.",
            life: 5000,
        });
    } finally {
        saving.value = false;
    }
};

const navigateToIntegrations = () => {
    router.push({ name: "integrationManagement" });
};

const contactSales = () => {
    window.open("https://stekz.com/contact", "_blank");
};
</script>

<style scoped lang="scss">
.stek-settings-page {
    max-width: 1400px;
    margin: 0 auto;

    .title-section {
        display: flex;
        align-items: center;
        gap: 1rem;

        .settings-icon {
            font-size: 2rem;
            color: var(--primary-color);
        }

        .page-title {
            margin: 0;
        }
    }
}

.enterprise-section {
    .enterprise-notice {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 1rem;
        background: var(--surface-50);
        border-radius: var(--border-radius);
        margin-bottom: 2rem;

        i {
            color: var(--primary-color);
            font-size: 1.25rem;
            margin-top: 0.125rem;
        }

        p {
            margin: 0;
            color: var(--text-color);

            a {
                color: var(--primary-color);
                text-decoration: none;
                font-weight: 500;

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }
}

.features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.feature-card {
    .feature-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        font-size: 1.125rem;

        .feature-icon {
            color: var(--primary-color);
            font-size: 1.5rem;
        }
    }

    .feature-description {
        color: var(--text-color-secondary);
        line-height: 1.6;
        margin-bottom: 1.5rem;
    }

    &.muted-feature {
        opacity: 0.7;
        filter: grayscale(0.5);
        background: var(--surface-50);
        border-style: dashed;
    }

    .prerequisite-message {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--orange-500);
        font-size: 0.875rem;
        margin-bottom: 1.5rem;
        padding: 0.5rem;
        background: var(--orange-50);
        border-radius: var(--border-radius);

        i {
            font-size: 1rem;
        }
    }

    .feature-toggle {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        .toggle-wrapper {
            display: flex;
            align-items: center;
            gap: 0.75rem;

            label {
                font-weight: 500;
                color: var(--text-color);
            }
        }
    }
}

.upgrade-message {
    margin-top: 2rem;

    .upgrade-content {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        strong {
            font-size: 1rem;
        }

        p {
            margin: 0;
            color: var(--text-color-secondary);
        }

        button {
            align-self: flex-start;
            margin-top: 0.5rem;
        }
    }
}

.integrations-section {
    .integrations-content {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;

        .section-description {
            color: var(--text-color-secondary);
            line-height: 1.6;
            margin: 0;
        }

        button {
            align-self: flex-start;
        }
    }
}

.general-section {
    .info-field {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
    }

    .info-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--text-color-secondary);
    }

    .info-value-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        background: var(--surface-50);
        border: 1px solid var(--surface-200);
        border-radius: var(--border-radius);
        padding: 0.625rem 0.75rem;
    }

    .info-value {
        font-family: monospace;
        font-size: 0.9rem;
        color: var(--text-color);
        flex: 1;
        word-break: break-all;
    }

    .copy-btn {
        flex-shrink: 0;
        min-width: 6.25rem;
        transition-property: scale;
        transition-duration: 150ms;
        transition-timing-function: cubic-bezier(0.2, 0, 0, 1);

        &:not(:disabled):active {
            scale: 0.96;
        }

        .copy-icon {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 1em;
            height: 1em;
        }

        .copy-icon-glyph {
            position: absolute;
            inset: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            scale: 0.25;
            filter: blur(4px);
            transition-property: opacity, scale, filter;
            transition-duration: 300ms;
            transition-timing-function: cubic-bezier(0.2, 0, 0, 1);

            &.is-active {
                opacity: 1;
                scale: 1;
                filter: blur(0);
            }
        }

        .copy-icon-check {
            color: var(--green-500);
        }
    }
}
</style>
